import { isProjectChangeExcludedFromProposal } from '../helpers/actions/apply-project-changes-with-proposal-context.helpers';
import { getChangeAffectedElementId } from '../helpers/actions/action.helpers';
import {
  getOverlapSlotKeys,
  compactActions,
} from '../helpers/actions/clean-actions.helpers';
import {
  getElementAndQuantityProperties,
  getElementProperties,
  isElementPropertySourceCategory,
} from '../helpers/element-property.helpers';
import { getElementQuantityProperties } from '../helpers/element-quantity-core.helpers';
import type {
  InactiveChangeReason,
  ProjectChange,
} from '../helpers/pipeline/update-engine.models';
import { ProjectChangeType } from '../helpers/pipeline/update-engine.models';
import type { Action } from '../helpers/pipeline/update-engine.models';
import {
  flattenElements,
  getChildElements,
  getElementById,
  isBuildingVersionElement,
  isElement,
} from '../helpers/recursive-element.helpers';
import type { Baseline, OneOfElements } from '../models/project.model';
import type { IProposal } from '../models/proposals.model';
import { throwValidationErrors } from './validation.helpers';
import { getProposalsInVersion } from '../helpers/proposal.helpers';
import { ProjectValidationErrors } from './project-validation';
import { getId } from '../helpers/object.helpers';
import type { ValidationResult } from './validation.models';
import { GenericValidationError } from './validation.models';
import { sortProposalChangesWithRecipeFirst } from '../helpers/actions/proposal-change-order.helpers';
import { resolveVersionForProposal } from '../helpers/actions/resolve-version-for-proposal.helpers';
import { applyElementCategoryProperties } from '../helpers/element-category.helpers';
import {
  clearRecipe,
  detachRecipe,
  overwriteRecipeForProposal,
} from '../helpers/recipe.helpers';
import type { ProjectChangeMap } from '../helpers/pipeline/update-engine.models';

const isValidProposal = (
  _buildingVersion: Baseline,
  proposal: IProposal,
): ValidationResult => {
  const { id, name, actionIds, resultsRecord: results } = proposal;
  if (!proposal) {
    return new GenericValidationError('Proposal is not defined');
  }

  if (!id || !actionIds || !results) {
    return new GenericValidationError(
      ProjectValidationErrors.PROPOSAL.MISSING_PROPERTIES,
    );
  }
  if (!name) {
    return new GenericValidationError(
      ProjectValidationErrors.PROPOSAL.MISSING_NAME,
    );
  }

  return true;
};

export const validateProposal = (
  version: Baseline,
  proposal: IProposal,
): IProposal => {
  throwValidationErrors(isValidProposal(version, proposal));
  return proposal;
};

export const isValidProposals = (version: Baseline): ValidationResult => {
  const proposals = getProposalsInVersion(version);

  if (!proposals.length) {
    return new GenericValidationError(
      ProjectValidationErrors.PROPOSAL.VERSION_HAS_NO_PROPOSALS,
    );
  }

  for (const proposal of proposals) {
    const valid = isValidProposal(version, proposal);
    if (valid !== true) {
      return valid;
    }
  }

  return true;
};

export const validateProposals = (version: Baseline): Baseline => {
  throwValidationErrors(isValidProposals(version));
  return version;
};

const collectReferencedActionIds = (proposals: IProposal[]): Set<string> => {
  const referenced = new Set<string>();
  for (const p of proposals) {
    for (const id of p.actionIds) {
      referenced.add(id);
    }
  }
  return referenced;
};

type VersionIdIndex = {
  elementIds: Set<string>;
  /** element id → set of property IDs (including quantity property IDs) */
  propertyIdsByElement: Map<string, Set<string>>;
  /** element id → set of element property *names* (excludes quantity properties) */
  propertyNamesByElement: Map<string, Set<string>>;
  /** element id → set of quantity property names (e.g. `area`, `energy`) */
  quantityNamesByElement: Map<string, Set<string>>;
  /** element id → set of direct child element IDs */
  childIdsByElement: Map<string, Set<string>>;
  /**
   * Category property names that exist on the resolved element only because a
   * later edit unlocked a conditional row. The recipe payload does not have them.
   * Used to undo a false `stale-property-name` mark (cast-in-place reinforcement).
   */
  recoverableCategoryPropertyNames: Map<string, Set<string>>;
};

/**
 * Builds a lookup index of all element, property, and child IDs in a version tree.
 * Used to validate that {@link ProjectChange} references point to existing entities.
 */
const buildVersionIdIndex = (version: Baseline): VersionIdIndex => {
  const allElements = flattenElements(version);
  const elementIds = new Set<string>(allElements.map(getId));
  const propertyIdsByElement = new Map<string, Set<string>>();
  const propertyNamesByElement = new Map<string, Set<string>>();
  const quantityNamesByElement = new Map<string, Set<string>>();
  const childIdsByElement = new Map<string, Set<string>>();
  const recoverableCategoryPropertyNames = new Map<string, Set<string>>();

  for (const element of allElements) {
    const propIds = new Set(
      getElementAndQuantityProperties(element).map(getId),
    );
    propertyIdsByElement.set(element.id, propIds);

    propertyNamesByElement.set(
      element.id,
      new Set(getElementProperties(element).map((p) => p.name)),
    );

    quantityNamesByElement.set(
      element.id,
      new Set(getElementQuantityProperties(element).map((p) => p.name)),
    );

    const childIds = new Set(
      getChildElements(element as OneOfElements).map(getId),
    );
    childIdsByElement.set(element.id, childIds);
  }

  return {
    elementIds,
    propertyIdsByElement,
    propertyNamesByElement,
    quantityNamesByElement,
    childIdsByElement,
    recoverableCategoryPropertyNames,
  };
};

/**
 * Change types that reference {@link ElementActivate} — the target element may not exist
 * in the tree yet because it is being added by a preceding action in the same proposal.
 * We skip elementId existence checks for these.
 */
const ELEMENT_ID_SKIP_TYPES: ReadonlySet<ProjectChangeType> = new Set([
  ProjectChangeType.ElementActivate,
]);

/**
 * Change types that list nested ids (property / child) under {@link change.elementId}.
 * These are validated in dedicated branches so a missing parent element or index slice
 * cannot be skipped by an empty map lookup.
 */
const ELEMENT_CHANGE_TYPES_WITH_NESTED_INDEXED_IDS: ReadonlySet<ProjectChangeType> =
  new Set([
    ProjectChangeType.ElementPropertyRemove,
    ProjectChangeType.ElementCategoryEnrich,
  ]);

/**
 * Element-property change types that carry a {@link change.propertyName} pointing at a
 * property row on the target element. Validated against baseline rows plus any names
 * added earlier in the same proposal (see {@link buildProposalPendingPropertyAdds}).
 */
const PROPERTY_NAME_REFERENCING_TYPES: ReadonlySet<ProjectChangeType> = new Set(
  [
    ProjectChangeType.ElementPropertyCount,
    ProjectChangeType.ElementPropertyFallbackCount,
    ProjectChangeType.ElementPropertyResolved,
    ProjectChangeType.ElementPropertyExpression,
    ProjectChangeType.ElementPropertyValue,
    ProjectChangeType.ElementPropertyFormatted,
    ProjectChangeType.ElementPropertyRename,
  ],
);

/**
 * Element-quantity change types that carry a {@link change.quantityName}. Quantity rows
 * are added/removed by category enrichment, never by proposal-scoped changes (those types
 * are baseline-only), so validating against the baseline tree is sufficient.
 */
const QUANTITY_NAME_REFERENCING_TYPES: ReadonlySet<ProjectChangeType> = new Set(
  [
    ProjectChangeType.ElementQuantityCount,
    ProjectChangeType.ElementQuantityFallbackCount,
    ProjectChangeType.ElementQuantityResolved,
    ProjectChangeType.ElementQuantityExpression,
    ProjectChangeType.ElementQuantityFallbackExpression,
    ProjectChangeType.ElementQuantityValue,
    ProjectChangeType.ElementQuantityFormatted,
  ],
);

/**
 * Maps each element id to the set of property names introduced earlier in the same
 * proposal via {@link ProjectChangeType.ElementCategoryEnrich.addProperties}. Used to
 * allow follow-up edits on those names to validate even though they are missing from
 * the baseline tree (e.g. proposal flips `heating_type=heat_pump` → enrich adds
 * `heat_source` → next action sets `heat_source=air`).
 *
 * `ElementPropertyAdd` is intentionally excluded — that change type is baseline-only
 * (see {@link PROJECT_CHANGE_TYPES_EXCLUDED_FROM_PROPOSAL}) and cannot appear inside a
 * proposal action.
 */
const buildProposalPendingPropertyAdds = (
  proposal: IProposal,
  actionById: ReadonlyMap<string, Action>,
): ReadonlyMap<string, ReadonlySet<string>> => {
  const map = new Map<string, Set<string>>();
  for (const actionId of proposal.actionIds) {
    const action = actionById.get(actionId);
    if (!action) continue;
    for (const change of action.changes) {
      if (change.inactive) continue;
      if (change.type !== ProjectChangeType.ElementCategoryEnrich) continue;
      if (change.addProperties.length === 0) continue;
      const set = map.get(change.elementId) ?? new Set<string>();
      for (const p of change.addProperties) set.add(p.name);
      map.set(change.elementId, set);
    }
  }
  return map;
};

/**
 * Validates that all entity references inside a single {@link ProjectChange} resolve to
 * existing nodes/properties in the version tree described by {@link VersionIdIndex}.
 *
 * `proposalPendingPropertyAdds` lets a follow-up edit reference a property name that an
 * earlier `ElementCategoryEnrich` in the same proposal just introduced.
 */
const isValidChangeReferences = (
  change: ProjectChange,
  index: VersionIdIndex,
  actionId: string,
  proposalPendingPropertyAdds: ReadonlyMap<
    string,
    ReadonlySet<string>
  > = new Map(),
): ValidationResult => {
  // Property removes: require the parent element and a property-id slice before checking each id
  if (change.type === ProjectChangeType.ElementPropertyRemove) {
    const targetElementId = change.elementId;
    if (!index.elementIds.has(targetElementId)) {
      return new GenericValidationError(
        `${ProjectValidationErrors.PROPOSAL.STALE_ELEMENT_REFERENCE}: action ${actionId}, ${change.type} targets element "${targetElementId}"`,
      );
    }
    const validProps = index.propertyIdsByElement.get(targetElementId);
    if (!validProps) {
      return new GenericValidationError(
        `${ProjectValidationErrors.PROPOSAL.STALE_ELEMENT_REFERENCE}: action ${actionId}, ${change.type} targets element "${targetElementId}"`,
      );
    }
    for (const propId of change.propertyIds) {
      if (!validProps.has(propId)) {
        return new GenericValidationError(
          `${ProjectValidationErrors.PROPOSAL.STALE_PROPERTY_REFERENCE}: action ${actionId}, ${change.type} targets property "${propId}" on element "${change.elementId}"`,
        );
      }
    }
  }

  // Category enrich removes: same ordering — parent element and child slice before each child id
  if (change.type === ProjectChangeType.ElementCategoryEnrich) {
    const targetElementId = change.elementId;
    if (!index.elementIds.has(targetElementId)) {
      return new GenericValidationError(
        `${ProjectValidationErrors.PROPOSAL.STALE_ELEMENT_REFERENCE}: action ${actionId}, ${change.type} targets element "${targetElementId}"`,
      );
    }
    const validChildren = index.childIdsByElement.get(targetElementId);
    if (!validChildren) {
      return new GenericValidationError(
        `${ProjectValidationErrors.PROPOSAL.STALE_ELEMENT_REFERENCE}: action ${actionId}, ${change.type} targets element "${targetElementId}"`,
      );
    }
    for (const childId of change.removeChildIds) {
      if (!validChildren.has(childId)) {
        return new GenericValidationError(
          `${ProjectValidationErrors.PROPOSAL.STALE_CHILD_REFERENCE}: action ${actionId}, ${change.type} targets child "${childId}" on element "${change.elementId}"`,
        );
      }
    }
    // `removePropertyNames` is intentionally NOT validated against existing rows.
    // The set may legitimately include property names that exist only in an
    // intermediate state of the proposal — e.g. metal `amount_scrap_based` is
    // derived implicitly by the category template when form=Pipe, then must be
    // listed for removal when a later action flips form to Wire. The baseline
    // tree and the final resolved tree both lack the row, so a name check here
    // produces false-positive `STALE_PROPERTY_NAME_REFERENCE` failures. The
    // enrich applier already tolerates names that hit nothing (no-op).
  }

  // Remaining elementId changes (nested-id types are handled above and excluded here)
  if (
    'elementId' in change &&
    !ELEMENT_ID_SKIP_TYPES.has(change.type) &&
    !ELEMENT_CHANGE_TYPES_WITH_NESTED_INDEXED_IDS.has(change.type)
  ) {
    const targetElementId = change.elementId;
    if (!index.elementIds.has(targetElementId)) {
      return new GenericValidationError(
        `${ProjectValidationErrors.PROPOSAL.STALE_ELEMENT_REFERENCE}: action ${actionId}, ${change.type} targets element "${targetElementId}"`,
      );
    }
  }

  // Property-name references: the change targets a property row that must exist on the
  // baseline element OR have been added earlier in the same proposal via ElementCategoryEnrich.
  // Catches orphan edits left behind when a category change drops the driving select option.
  if (PROPERTY_NAME_REFERENCING_TYPES.has(change.type)) {
    const targetElementId = (change as { elementId: string }).elementId;
    const propertyName = (change as { propertyName: string }).propertyName;
    const validNames = index.propertyNamesByElement.get(targetElementId);
    const pendingForElement =
      proposalPendingPropertyAdds.get(targetElementId) ?? new Set<string>();
    if (
      !validNames?.has(propertyName) &&
      !pendingForElement.has(propertyName)
    ) {
      return new GenericValidationError(
        `${ProjectValidationErrors.PROPOSAL.STALE_PROPERTY_NAME_REFERENCE}: action ${actionId}, ${change.type} targets property "${propertyName}" on element "${targetElementId}"`,
      );
    }
  }

  // Quantity-name references: quantity rows are added/removed only by baseline-only
  // changes (category enrichment runs at the baseline layer), so the baseline tree is
  // authoritative. No pending-adds bypass is needed.
  if (QUANTITY_NAME_REFERENCING_TYPES.has(change.type)) {
    const targetElementId = (change as { elementId: string }).elementId;
    const quantityName = (change as { quantityName: string }).quantityName;
    const validNames = index.quantityNamesByElement.get(targetElementId);
    if (!validNames?.has(quantityName)) {
      return new GenericValidationError(
        `${ProjectValidationErrors.PROPOSAL.STALE_QUANTITY_NAME_REFERENCE}: action ${actionId}, ${change.type} targets quantity "${quantityName}" on element "${targetElementId}"`,
      );
    }
  }

  return true;
};

/**
 * Registers one element subtree on {@link VersionIdIndex} (ids, property names, quantities, children).
 */
const addElementSubtreeToVersionIdIndex = (
  index: VersionIdIndex,
  root: OneOfElements,
): void => {
  for (const node of flattenElements(root)) {
    const indexedNode = isElement(node)
      ? applyElementCategoryProperties(node)
      : node;
    index.elementIds.add(indexedNode.id);
    index.propertyIdsByElement.set(
      indexedNode.id,
      new Set(getElementAndQuantityProperties(indexedNode).map(getId)),
    );
    index.propertyNamesByElement.set(
      indexedNode.id,
      new Set(getElementProperties(indexedNode).map((p) => p.name)),
    );
    index.quantityNamesByElement.set(
      indexedNode.id,
      new Set(getElementQuantityProperties(indexedNode).map((p) => p.name)),
    );
    if (isElement(indexedNode)) {
      index.childIdsByElement.set(
        indexedNode.id,
        new Set(getChildElements(indexedNode).map(getId)),
      );
    }
  }
};

/**
 * Removes descendant nodes from {@link VersionIdIndex}, optionally keeping the root id.
 */
const removeElementSubtreeFromVersionIdIndex = (
  index: VersionIdIndex,
  root: OneOfElements,
  keepRootId: boolean,
): void => {
  for (const node of flattenElements(root)) {
    if (keepRootId && node.id === root.id) continue;
    index.elementIds.delete(node.id);
    index.propertyIdsByElement.delete(node.id);
    index.propertyNamesByElement.delete(node.id);
    index.quantityNamesByElement.delete(node.id);
    index.childIdsByElement.delete(node.id);
    index.recoverableCategoryPropertyNames.delete(node.id);
  }
};

type RecipeStructuralChange =
  | ProjectChangeMap[ProjectChangeType.ElementRecipeApply]
  | ProjectChangeMap[ProjectChangeType.ElementRecipeDetach]
  | ProjectChangeMap[ProjectChangeType.ElementRecipeClear];

const isRecipeStructuralChange = (
  change: ProjectChange,
): change is RecipeStructuralChange =>
  change.type === ProjectChangeType.ElementRecipeApply ||
  change.type === ProjectChangeType.ElementRecipeDetach ||
  change.type === ProjectChangeType.ElementRecipeClear;

/**
 * Applies every proposal-scoped recipe structural change to {@link VersionIdIndex} before
 * reference validation so quantity/property edits on deterministic recipe child ids validate
 * even when their action is listed before {@link ProjectChangeType.ElementRecipeApply} in
 * {@link IProposal.actionIds} (DEV-3293, DEV-3313).
 */
const extendIndexWithProposalRecipeStructuralChanges = (
  index: VersionIdIndex,
  version: Baseline,
  actionById: ReadonlyMap<string, Action>,
  proposal: IProposal,
  resolvedVersion: Baseline,
): void => {
  for (const actionId of proposal.actionIds) {
    const action = actionById.get(actionId);
    if (!action) continue;
    for (const change of sortProposalChangesWithRecipeFirst(action.changes)) {
      if (change.inactive) continue;
      if (!isRecipeStructuralChange(change)) continue;
      extendVersionIdIndexWithRecipeStructuralChange(
        index,
        version,
        change,
        resolvedVersion,
      );
    }
  }
};

/**
 * Registers a baseline or proposal-resolved node on {@link VersionIdIndex}.
 * Recipe-scoped product rows only need their id in `elementIds` (DEV-3316).
 */
const registerResolvedNodeInVersionIdIndex = (
  index: VersionIdIndex,
  node: OneOfElements,
): void => {
  if (isBuildingVersionElement(node)) return;
  if (isElement(node)) {
    addElementSubtreeToVersionIdIndex(
      index,
      applyElementCategoryProperties(node),
    );
    return;
  }
  index.elementIds.add(node.id);
};

/**
 * Ensures every {@link getChangeAffectedElementId} on this proposal is indexed when the
 * node exists on baseline or the proposal-resolved tree (DEV-3313).
 */
const ensureProposalChangeTargetsInVersionIdIndex = (
  index: VersionIdIndex,
  version: Baseline,
  resolvedVersion: Baseline,
  proposal: IProposal,
  actionById: ReadonlyMap<string, Action>,
): void => {
  for (const actionId of proposal.actionIds) {
    const action = actionById.get(actionId);
    if (!action) continue;
    for (const change of action.changes) {
      if (change.inactive) continue;
      const elementId = getChangeAffectedElementId(change);
      if (!elementId || index.elementIds.has(elementId)) continue;

      for (const tree of [resolvedVersion, version] as const) {
        const node = getElementById(tree, elementId, false);
        if (!node) continue;
        registerResolvedNodeInVersionIdIndex(index, node);
        break;
      }
    }
  }
};

/**
 * Adds ids (and quantity/property name sets) from the proposal-resolved tree when they are
 * missing on baseline or when resolved rows expose quantities the baseline snapshot lacks
 * (DEV-3313).
 */
const mergeResolvedProposalSubtreeIntoVersionIdIndex = (
  index: VersionIdIndex,
  version: Baseline,
  proposal: IProposal,
): void => {
  const { version: resolved } = resolveVersionForProposal(version, proposal);
  if (resolved === version) return;

  for (const node of flattenElements(resolved)) {
    if (isBuildingVersionElement(node)) continue;
    if (!isElement(node)) {
      index.elementIds.add(node.id);
      continue;
    }
    const indexedNode = applyElementCategoryProperties(node);
    if (!index.elementIds.has(indexedNode.id)) {
      addElementSubtreeToVersionIdIndex(index, indexedNode);
      continue;
    }
    // Union baseline with resolved names so `PROPERTY_NAME_REFERENCING_TYPES`
    // (e.g. ElementPropertyCount) still validate against rows that exist only
    // on the proposal-resolved tree (DEV-3316).
    const resolvedQuantityNames = new Set(
      getElementQuantityProperties(indexedNode).map((p) => p.name),
    );
    const existingQuantityNames =
      index.quantityNamesByElement.get(indexedNode.id) ?? new Set<string>();
    index.quantityNamesByElement.set(
      indexedNode.id,
      new Set([...existingQuantityNames, ...resolvedQuantityNames]),
    );

    const resolvedPropertyNames = new Set(
      getElementProperties(indexedNode).map((p) => p.name),
    );
    const existingPropertyNames =
      index.propertyNamesByElement.get(indexedNode.id) ?? new Set<string>();
    index.propertyNamesByElement.set(
      indexedNode.id,
      new Set([...existingPropertyNames, ...resolvedPropertyNames]),
    );
  }
};

/**
 * Baseline tree index extended with recipe subtrees and proposal-resolved nodes.
 * Used when validating actions that are not linked on any {@link IProposal.actionIds}.
 */
const buildProposalRecipeAwareVersionIdIndex = (
  version: Baseline,
  proposals: IProposal[],
  actionById: ReadonlyMap<string, Action>,
): VersionIdIndex => {
  const index = buildVersionIdIndex(version);
  for (const proposal of proposals) {
    const { version: resolved } = resolveVersionForProposal(version, proposal);
    extendIndexWithProposalRecipeStructuralChanges(
      index,
      version,
      actionById,
      proposal,
      resolved,
    );
    mergeResolvedProposalSubtreeIntoVersionIdIndex(index, version, proposal);
    ensureProposalChangeTargetsInVersionIdIndex(
      index,
      version,
      resolved,
      proposal,
      actionById,
    );
  }
  return index;
};

/**
 * Union category property names from the resolved subtree into {@link index}.
 * Names already recorded from the recipe payload stay. Resolved-only names
 * (conditional rows unlocked by a later property edit) are added.
 *
 * @param index - Proposal reference index being rebuilt after a recipe structural change
 * @param resolvedRoot - Same parent on the proposal-resolved tree
 */
const unionCategoryPropertyNamesFromResolved = (
  index: VersionIdIndex,
  resolvedRoot: OneOfElements,
): void => {
  for (const node of flattenElements(resolvedRoot)) {
    if (!isElement(node) || !index.elementIds.has(node.id)) continue;
    const categorized = applyElementCategoryProperties(node);
    const names =
      index.propertyNamesByElement.get(node.id) ?? new Set<string>();
    // Only names the recipe payload lacks. A property that already exists there,
    // or a user property that later reuses the name, must not revive an old edit.
    const recoverable = new Set<string>();
    for (const property of getElementProperties(categorized)) {
      if (
        !names.has(property.name) &&
        isElementPropertySourceCategory(property)
      ) {
        recoverable.add(property.name);
      }
      names.add(property.name);
    }
    index.propertyNamesByElement.set(node.id, names);
    index.recoverableCategoryPropertyNames.set(node.id, recoverable);
  }
};

/**
 * After a proposal-scoped recipe apply, refresh index entries for the parent's subtree so
 * later changes can target deterministic recipe child ids (DEV-3293).
 */
const extendVersionIdIndexWithRecipeStructuralChange = (
  index: VersionIdIndex,
  version: Baseline,
  change: RecipeStructuralChange,
  resolvedVersion?: Baseline,
): void => {
  const parent =
    getElementById(version, change.elementId, false) ??
    (resolvedVersion
      ? getElementById(resolvedVersion, change.elementId, false)
      : undefined);
  if (!parent || !isElement(parent)) return;

  removeElementSubtreeFromVersionIdIndex(index, parent, true);

  if (change.type === ProjectChangeType.ElementRecipeApply) {
    addElementSubtreeToVersionIdIndex(
      index,
      overwriteRecipeForProposal(parent, change.recipe),
    );
  } else if (change.type === ProjectChangeType.ElementRecipeDetach) {
    addElementSubtreeToVersionIdIndex(index, detachRecipe(parent));
  } else {
    addElementSubtreeToVersionIdIndex(index, clearRecipe(parent));
  }

  // The recipe payload is the pre-edit template. A later type change (floor plate
  // → cast-in-place) adds properties such as reinforcement_steel only on the
  // resolved tree. Re-indexing from the payload drops those names and the
  // follow-up value is marked stale-property-name.
  const resolvedParent = resolvedVersion
    ? getElementById(resolvedVersion, change.elementId, false)
    : undefined;
  if (resolvedParent) {
    unionCategoryPropertyNamesFromResolved(index, resolvedParent);
  }
};

type ProposalChangeWalkContext = {
  index: VersionIdIndex;
  actionId: string;
  pendingAdds: ReadonlyMap<string, ReadonlySet<string>>;
};

/**
 * Clear {@link InactiveChangeMeta} when a `stale-property-name` mark was a false
 * positive: the recipe payload lacked a conditional category row (reinforcement
 * steel after the type becomes cast-in-place) that the resolved element now has.
 * A name that merely reappears, on a new property or a reused recipe slot whose
 * template already has the row, stays inactive. Element-id deactivation stays one-way.
 *
 * @param change - Stored proposal change
 * @param context - Index for this walk step
 */
const reactivateStalePropertyName = (
  change: ProjectChange,
  { index }: ProposalChangeWalkContext,
): ProjectChange => {
  if (change.inactive?.reason !== 'stale-property-name') return change;
  if (!PROPERTY_NAME_REFERENCING_TYPES.has(change.type)) return change;
  if (!('elementId' in change) || !('propertyName' in change)) return change;

  const recoverable = index.recoverableCategoryPropertyNames.get(
    change.elementId,
  );
  if (!recoverable?.has(change.propertyName)) return change;

  const { inactive: _inactive, ...active } = change;
  return active;
};

/**
 * Walks every stored action change in {@link IProposal.actionIds} order with a cumulative
 * {@link VersionIdIndex}: baseline first, then each kept recipe structural change extends
 * the index so edits on proposal-only recipe children see their targets (DEV-3293).
 * Orphan actions (not referenced by any proposal) are visited last against a
 * proposal-recipe-aware index.
 *
 * Changes marked {@link InactiveChangeMeta} are passed through untouched and never
 * visited — deactivation is one-way (DEV-3640).
 *
 * `mapChange` returns the change to keep (possibly transformed) or `undefined` to drop it.
 * Returns the original version when no change was transformed or dropped.
 */
const mapProposalActionChanges = (
  version: Baseline,
  mapChange: (
    change: ProjectChange,
    context: ProposalChangeWalkContext,
  ) => ProjectChange | undefined,
): { version: Baseline; changed: boolean } => {
  const actions = version.actions;
  if (!actions?.length) return { version, changed: false };

  const proposals = version.proposals ?? [];
  const actionById = new Map(actions.map((action) => [action.id, action]));
  const nextChangesByActionId = new Map<string, ProjectChange[]>();

  let changed = false;
  const visitedActionIds = new Set<string>();

  const visitChange = (
    change: ProjectChange,
    context: ProposalChangeWalkContext,
  ): ProjectChange | undefined => {
    // A name marked stale while the recipe template lacked it can be valid again
    // once the resolved element has the row (cast-in-place reinforcement).
    const candidate = reactivateStalePropertyName(change, context);
    if (candidate.inactive) {
      if (candidate !== change) changed = true;
      return candidate;
    }
    const mapped = mapChange(candidate, context);
    if (mapped !== change) changed = true;
    return mapped;
  };

  for (const proposal of proposals) {
    const pendingAdds = buildProposalPendingPropertyAdds(proposal, actionById);
    const index = buildVersionIdIndex(version);
    const { version: resolved } = resolveVersionForProposal(version, proposal);
    extendIndexWithProposalRecipeStructuralChanges(
      index,
      version,
      actionById,
      proposal,
      resolved,
    );
    mergeResolvedProposalSubtreeIntoVersionIdIndex(index, version, proposal);
    ensureProposalChangeTargetsInVersionIdIndex(
      index,
      version,
      resolved,
      proposal,
      actionById,
    );

    for (const actionId of proposal.actionIds) {
      if (visitedActionIds.has(actionId)) continue;
      const action = actionById.get(actionId);
      if (!action) continue;
      visitedActionIds.add(actionId);

      const kept: ProjectChange[] = [];
      for (const change of sortProposalChangesWithRecipeFirst(action.changes)) {
        const mapped = visitChange(change, {
          index,
          actionId: action.id,
          pendingAdds,
        });
        if (!mapped) continue;
        kept.push(mapped);
        if (isRecipeStructuralChange(mapped) && !mapped.inactive) {
          extendVersionIdIndexWithRecipeStructuralChange(
            index,
            version,
            mapped,
            resolved,
          );
        }
      }
      nextChangesByActionId.set(action.id, kept);
    }
  }

  const orphanIndex = buildProposalRecipeAwareVersionIdIndex(
    version,
    proposals,
    actionById,
  );
  const emptyPendingAdds = new Map<string, ReadonlySet<string>>();
  for (const action of actions) {
    if (visitedActionIds.has(action.id)) continue;
    const kept: ProjectChange[] = [];
    for (const change of action.changes) {
      const mapped = visitChange(change, {
        index: orphanIndex,
        actionId: action.id,
        pendingAdds: emptyPendingAdds,
      });
      if (mapped) kept.push(mapped);
    }
    nextChangesByActionId.set(action.id, kept);
  }

  if (!changed) return { version, changed: false };

  const nextActions = actions.map((action) => ({
    ...action,
    changes: nextChangesByActionId.get(action.id) ?? action.changes,
  }));

  return { version: { ...version, actions: nextActions }, changed: true };
};

/**
 * Drops stored proposal {@link ProjectChange} rows that fail
 * {@link isValidChangeReferences} — same cumulative index walk as
 * {@link deactivateStaleProposalActionChanges}. Inactive rows are kept as-is.
 */
export const stripProposalChangesFailingValidation = (
  version: Baseline,
): { version: Baseline; removed: number } => {
  let removed = 0;
  const { version: next, changed } = mapProposalActionChanges(
    version,
    (change, { index, actionId, pendingAdds }) => {
      if (
        isValidChangeReferences(change, index, actionId, pendingAdds) !== true
      ) {
        removed += 1;
        return undefined;
      }
      return change;
    },
  );
  if (!changed) return { version, removed: 0 };
  return { version: compactActions(next), removed };
};

/** One stale reference degraded by {@link deactivateStaleProposalActionChanges}. */
export type StaleChangeDegradation = {
  actionId: string;
  changeType: ProjectChangeType;
  elementId: string | undefined;
  reason: InactiveChangeReason;
  /** `true` when the whole change was marked inactive, `false` when only dead ids were filtered out */
  deactivated: boolean;
};

/**
 * Degrades one active change whose references went stale (DEV-3640):
 *
 * - dead target `elementId` → whole change marked inactive (`stale-element`)
 * - dead ids in {@link ProjectChangeMap[ProjectChangeType.ElementPropertyRemove].propertyIds} /
 *   {@link ProjectChangeMap[ProjectChangeType.ElementCategoryEnrich].removeChildIds} with a live
 *   parent → dead ids filtered out, change stays active
 * - dead `propertyName` / `quantityName` → whole change marked inactive
 *
 * Returns the change untouched when all references resolve.
 */
const degradeStaleChangeReferences = (
  change: ProjectChange,
  { index, actionId, pendingAdds }: ProposalChangeWalkContext,
  deactivatedAt: string,
  onDegraded: (degradation: StaleChangeDegradation) => void,
): ProjectChange => {
  const report = (reason: InactiveChangeReason, deactivated: boolean) =>
    onDegraded({
      actionId,
      changeType: change.type,
      elementId: getChangeAffectedElementId(change),
      reason,
      deactivated,
    });
  const deactivate = (reason: InactiveChangeReason): ProjectChange => {
    report(reason, true);
    return { ...change, inactive: { reason, at: deactivatedAt } };
  };

  if (
    'elementId' in change &&
    !ELEMENT_ID_SKIP_TYPES.has(change.type) &&
    !index.elementIds.has(change.elementId)
  ) {
    return deactivate('stale-element');
  }

  if (change.type === ProjectChangeType.ElementPropertyRemove) {
    const validProps = index.propertyIdsByElement.get(change.elementId);
    const keptIds = change.propertyIds.filter((id) => validProps?.has(id));
    if (keptIds.length === change.propertyIds.length) return change;
    if (keptIds.length === 0) return deactivate('stale-property');
    report('stale-property', false);
    return { ...change, propertyIds: keptIds };
  }

  if (change.type === ProjectChangeType.ElementCategoryEnrich) {
    const validChildren = index.childIdsByElement.get(change.elementId);
    const keptIds = change.removeChildIds.filter((id) =>
      validChildren?.has(id),
    );
    if (keptIds.length === change.removeChildIds.length) return change;
    report('stale-child', false);
    return { ...change, removeChildIds: keptIds };
  }

  if (PROPERTY_NAME_REFERENCING_TYPES.has(change.type)) {
    const { elementId, propertyName } = change as {
      elementId: string;
      propertyName: string;
    };
    const validNames = index.propertyNamesByElement.get(elementId);
    const pendingForElement = pendingAdds.get(elementId);
    if (
      !validNames?.has(propertyName) &&
      !pendingForElement?.has(propertyName)
    ) {
      return deactivate('stale-property-name');
    }
  }

  if (QUANTITY_NAME_REFERENCING_TYPES.has(change.type)) {
    const { elementId, quantityName } = change as {
      elementId: string;
      quantityName: string;
    };
    if (!index.quantityNamesByElement.get(elementId)?.has(quantityName)) {
      return deactivate('stale-quantity-name');
    }
  }

  return change;
};

/**
 * Marks stored proposal changes whose references went stale as {@link InactiveChangeMeta}
 * and filters dead ids out of nested id lists — degradation on the smallest unit instead
 * of failing the whole version (DEV-3640).
 *
 * Stale references arise retroactively: positional ids (`parent:r:recipeId:slot:index`,
 * `:p:index`) shift or die when the baseline is edited, so a change that was valid when
 * recorded can orphan later. Save-time validation cannot prevent that, hence this runs in
 * the enrich pipeline before {@link validateProject}.
 *
 * Already-inactive changes are never touched (no reactivation — positional ids are reused,
 * a revived id may belong to a different element). Inactive changes are GC:ed when a
 * proposal is squashed to baseline (`buildBaselineVersionFromProposal` clears all actions).
 */
export const deactivateStaleProposalActionChanges = (
  version: Baseline,
): { version: Baseline; degradations: StaleChangeDegradation[] } => {
  const degradations: StaleChangeDegradation[] = [];
  const deactivatedAt = new Date().toISOString();
  const { version: next } = mapProposalActionChanges(
    version,
    (change, context) =>
      degradeStaleChangeReferences(change, context, deactivatedAt, (d) =>
        degradations.push(d),
      ),
  );
  return { version: next, degradations };
};

/**
 * Ensures every entry in {@link Baseline.actions} is listed in at least one
 * {@link IProposal.actionIds}, every referenced id exists on the version, and no action
 * contains changes for which {@link isProjectChangeExcludedFromProposal} is true (baseline-only).
 *
 * Actions that exist on the version but are not referenced by any proposal fail with
 * {@link ProjectValidationErrors.PROPOSAL.ORPHAN_ACTIONS}; unknown ids on a proposal fail with
 * {@link ProjectValidationErrors.PROPOSAL.UNKNOWN_ACTION_IDS}. Project-wide validation (`validateProject`) turns failures into throws.
 */
export const isValidProposalsAndActions = (
  version: Baseline,
): ValidationResult => {
  const actions = version.actions;
  const proposals = version.proposals ?? [];

  const actionIdsOnVersion = new Set((actions ?? []).map((a) => a.id));

  for (const p of proposals) {
    for (const actionId of p.actionIds) {
      if (!actionIdsOnVersion.has(actionId)) {
        return new GenericValidationError(
          `${ProjectValidationErrors.PROPOSAL.UNKNOWN_ACTION_IDS}: ${actionId}`,
        );
      }
    }
  }

  if (!actions?.length) return true;

  for (const action of actions) {
    // Leave no empty actions
    if (action.changes.length === 0) {
      return new GenericValidationError(
        `${ProjectValidationErrors.PROPOSAL.EMPTY_ACTIONS}: action ${action.id}`,
      );
    }
    for (const change of action.changes) {
      if (change.inactive) continue;
      if (isProjectChangeExcludedFromProposal(change)) {
        const detail =
          'propertyName' in change && change.propertyName !== undefined
            ? `${change.type} (${String(change.propertyName)})`
            : change.type;
        return new GenericValidationError(
          `${ProjectValidationErrors.PROPOSAL.FORBIDDEN_CHANGES_IN_PROPOSAL_ACTIONS}: action ${action.id}, change ${detail}`,
        );
      }
    }
  }

  // Stale entity references are deliberately NOT fatal here: positional ids in stored
  // changes go stale retroactively when the baseline is edited, and a hard failure
  // write-locks every save of the project (DEV-3640). They are degraded per change by
  // {@link deactivateStaleProposalActionChanges} in the enrich pipeline instead.

  const referenced = collectReferencedActionIds(proposals);
  const orphanIds = actions
    .filter((a) => !referenced.has(a.id))
    .map((a) => a.id);
  if (orphanIds.length) {
    return new GenericValidationError(
      `${ProjectValidationErrors.PROPOSAL.ORPHAN_ACTIONS}: ${orphanIds.join(', ')}`,
    );
  }

  // Ensure no proposal contains two actions that cover the same element+slot.
  // Duplicate slots indicate that the overlap-subtraction logic failed and two
  // actions would both alter the same property/quantity/visibility for an element.
  const duplicateSlot = findDuplicateSlotInProposals(proposals, actions);
  if (duplicateSlot) return duplicateSlot;

  const duplicateChangeTypeInAction =
    findDuplicateChangeTypeInSameActionSlot(actions);
  if (duplicateChangeTypeInAction) return duplicateChangeTypeInAction;

  return true;
};

/**
 * Scans each proposal for the same `${elementId}:${overlapSlotKey}` claimed by
 * **more than one action**. Multiple granular changes inside a **single** action
 * may share one overlap slot (e.g. {@link ProjectChangeType.ElementQuantityValue}
 * and {@link ProjectChangeType.ElementQuantityExpression} for the same quantity);
 * that is valid. The first cross-action duplicate found is returned as a
 * {@link GenericValidationError}; returns `undefined` when everything is clean.
 */
/**
 * Rejects when a single {@link Action} stores the same change type twice for one
 * overlap slot (e.g. two {@link ProjectChangeType.ElementQuantityValue} rows for `mass`).
 */
const findDuplicateChangeTypeInSameActionSlot = (
  actions: Action[],
): GenericValidationError | undefined => {
  for (const action of actions) {
    const seen = new Map<string, ProjectChangeType>();
    for (const change of action.changes) {
      if (change.inactive) continue;
      const elementId = getChangeAffectedElementId(change);
      if (!elementId) continue;
      for (const slot of getOverlapSlotKeys(change)) {
        const key = `${elementId}:${slot}:${change.type}`;
        if (seen.has(key)) {
          return new GenericValidationError(
            `${ProjectValidationErrors.PROPOSAL.DUPLICATE_CHANGE_TYPE_IN_ACTION_SLOT}: action "${action.id}", element "${elementId}", slot "${slot}", type "${change.type}"`,
          );
        }
        seen.set(key, change.type);
      }
    }
  }
  return undefined;
};

const findDuplicateSlotInProposals = (
  proposalsList: IProposal[],
  actions: Action[],
): GenericValidationError | undefined => {
  const actionById = new Map(actions.map((a) => [a.id, a]));

  for (const proposal of proposalsList) {
    const slotOwnerActionId = new Map<string, string>();
    for (const actionId of proposal.actionIds) {
      const action = actionById.get(actionId);
      if (!action) continue;
      for (const change of action.changes) {
        if (change.inactive) continue;
        const elementId = getChangeAffectedElementId(change);
        if (!elementId) continue;
        for (const slot of getOverlapSlotKeys(change)) {
          const key = `${elementId}:${slot}`;
          const owner = slotOwnerActionId.get(key);
          if (owner !== undefined && owner !== actionId) {
            return new GenericValidationError(
              `${ProjectValidationErrors.PROPOSAL.DUPLICATE_SLOT_IN_PROPOSAL}: proposal "${proposal.id}", element "${elementId}", slot "${slot}"`,
            );
          }
          if (owner === undefined) slotOwnerActionId.set(key, actionId);
        }
      }
    }
  }
  return undefined;
};
